import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Group,
  Modal,
  ScrollAreaAutosize,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useUpdateFollowUp } from "@hooks/mutations/followUp/useUpdateFollowUp";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { FollowUp } from "@customTypes/followUp";


interface ModalProps {
  opened: boolean;
  close: () => void;
  followUp: Partial<FollowUp> | null;
}

export const EditFollowUpModal = ({ opened, close, followUp }: ModalProps) => {
  const { t } = useTranslation();

  const [followUpDate, setFollowUpDate] = useState<Date | null>(new Date());
  const [reason, setReason] = useState("");
  const [followUpId, setFollowUpId] = useState("");

  const { mutate: updateFollowUp, isLoading: isUpdateFollowUpLoading } =
    useUpdateFollowUp();

  const handleErrors = useQueryErrorHandling();

  const handleSubmit = () => {
    if (!reason || !followUpDate) return null;
    const id = showLoadingNotification({});

    updateFollowUp(
      {
        intent: "update",
        followUpId,
        reason,
        followUpDate: followUpDate!,
      },
      {
        onError: (res: any) => handleErrors(res, "mutation", id),
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "update-follow-up-success",
          });
          close();
        },
      }
    );
  };

  useEffect(() => {
    if (followUp?.id) {
      // console.log("Follow up:", followUp);
      setFollowUpDate(new Date(followUp?.followUpDate || new Date()));
      setReason(followUp?.reason || "");
      setFollowUpId(followUp?.id ?? "");
    }
  }, [followUp]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-follow-up")}</Text>}
      opened={opened}
      onClose={close}
      styles={{ body: { minHeight: "auto" } }}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Stack>
          <DatePickerInput
            withAsterisk
            label={t("follow-up-date")}
            value={followUpDate}
            onChange={setFollowUpDate}
          />
          <Textarea
            withAsterisk
            label={t("reason")}
            rows={5}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Stack>

        <Group justify="end" mt={"xl"} pb={"0"}>
          <Button
            onClick={handleSubmit}
            loading={isUpdateFollowUpLoading}
            disabled={!reason || !followUpDate}>
            {t("update")}
          </Button>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
